// =============================================================
// eprelClassification.ts
//
// Pure helpers for deriving normalized FixID classification
// fields from a raw EPREL product response.
//
// Mirrored on the edge function side (supabase/functions/eprel-search)
// so both ingestion paths stay consistent.
// =============================================================

export type ApplianceCategory =
  | "WASHING_MACHINE"
  | "TUMBLE_DRYER"
  | "DISHWASHER"
  | "WASHER_DRYER"
  | "UNKNOWN";

export type DryerTechnology = "heat_pump" | "condenser" | "vented" | null;

export type DishwasherInstall =
  | "freestanding"
  | "fully_integrated"
  | "semi_integrated"
  | null;

export type DishwasherWidth = "standard_60cm" | "slim_45cm" | null;

export interface ApplianceClassification {
  applianceCategory: ApplianceCategory;
  dryerTechnology: DryerTechnology;
  dishwasherInstall: DishwasherInstall;
  dishwasherWidth: DishwasherWidth;
  dishwasherCapacity: number | null;
  classificationSource: "eprel";
}

// EPREL endpoint path segments per FixID category.
export const EPREL_CATEGORY_ENDPOINTS: Record<
  Exclude<ApplianceCategory, "UNKNOWN">,
  string
> = {
  WASHING_MACHINE: "washingmachines2019",
  TUMBLE_DRYER: "tumbledryers2019",
  DISHWASHER: "dishwashers2019",
  WASHER_DRYER: "washerdriers2019",
};

const norm = (s: unknown): string =>
  typeof s === "string" ? s.trim().toLowerCase().replace(/[\s\-_]+/g, " ") : "";

function categoryFromEndpoint(eprelCategory: string): ApplianceCategory {
  const k = eprelCategory.toLowerCase();
  // Must be checked before the tumble-dryer branch: EPREL's washer-dryer
  // product group is "washerdriers2019", a genuinely separate appliance
  // category, not a dryer sub-type — it also carries no drying-technology
  // field in EPREL, unlike tumbledryers2019.
  if (k.includes("washerdrier") || k.includes("washer-dryer") || k.includes("washerdryer"))
    return "WASHER_DRYER";
  if (k.includes("washingmachines")) return "WASHING_MACHINE";
  if (k.includes("tumbledryers") || k.includes("dryer"))
    return "TUMBLE_DRYER";
  if (k.includes("dishwashers")) return "DISHWASHER";
  return "UNKNOWN";
}

function classifyDryerTech(value: unknown): DryerTechnology {
  const v = norm(value);
  if (!v) return null;
  if (v.includes("heat pump")) return "heat_pump";
  if (v.includes("vented") || v.includes("air dryer") || v === "air") return "vented";
  if (v.includes("condenser") || v.includes("condensation")) return "condenser";
  return null;
}

function classifyDishwasherInstall(value: unknown): DishwasherInstall {
  const v = norm(value);
  if (!v) return null;
  // Fast-path on exact normalized strings
  if (v === "free standing" || v === "freestanding") return "freestanding";
  if (v === "semi built in" || v === "semi integrated") return "semi_integrated";
  if (v === "fully integrated" || v === "built in" || v === "integrated")
    return "fully_integrated";
  // Substring fallback
  if (v.includes("free standing") || v.includes("freestanding"))
    return "freestanding";
  if (v.includes("semi"))
    return "semi_integrated";
  if (
    v.includes("fully integrated") ||
    v.includes("built in") ||
    v.includes("builtin") ||
    v.includes("integrated")
  )
    return "fully_integrated";
  return null;
}

function inferDishwasherWidth(
  capacity: number | null,
  rawWidth?: unknown,
): DishwasherWidth {
  // Explicit width takes priority when present
  if (typeof rawWidth === "string") {
    const v = norm(rawWidth);
    if (v.includes("45")) return "slim_45cm";
    if (v.includes("60")) return "standard_60cm";
  }
  if (typeof rawWidth === "number") {
    if (rawWidth > 0 && rawWidth <= 500) return "slim_45cm";
    if (rawWidth > 500) return "standard_60cm";
  }
  // EU norm: ≥12 place settings = standard 60cm, ≤11 = slim 45cm
  if (capacity != null) {
    return capacity >= 12 ? "standard_60cm" : "slim_45cm";
  }
  return null;
}

export function mapEprelToClassification(
  product: Record<string, unknown> | null | undefined,
  eprelCategory: string,
): ApplianceClassification {
  const cat = categoryFromEndpoint(eprelCategory);
  const empty: ApplianceClassification = {
    applianceCategory: cat,
    dryerTechnology: null,
    dishwasherInstall: null,
    dishwasherWidth: null,
    dishwasherCapacity: null,
    classificationSource: "eprel",
  };
  if (!product) return empty;

  const p = product as Record<string, unknown>;

  // Dryer
  if (cat === "TUMBLE_DRYER") {
    const tech =
      classifyDryerTech(p.technologyType) ??
      classifyDryerTech(p.technology) ??
      classifyDryerTech(p.dryerType);
    empty.dryerTechnology = tech;
  }

  // Dishwasher
  if (cat === "DISHWASHER") {
    const install =
      classifyDishwasherInstall(p.installationType) ??
      classifyDishwasherInstall(p.instalationType) ??
      classifyDishwasherInstall(p.type) ??
      classifyDishwasherInstall(p.designType);
    empty.dishwasherInstall = install;

    const capRaw =
      (p.ratedCapacityEco as number | undefined) ??
      (p.ratedCapacity as number | undefined);
    const cap =
      typeof capRaw === "number" && Number.isFinite(capRaw) ? Math.round(capRaw) : null;
    empty.dishwasherCapacity = cap;
    empty.dishwasherWidth = inferDishwasherWidth(cap, p.dimensionWidth);
  }

  return empty;
}
